import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'

import { download } from '../api/client'
import { endpoints } from '../api/endpoints'
import { Icon } from '../components/Icon'
import { PageHeader, Stat } from '../components/Page'
import { dayIn, formatDay, phoneTime, rangeFor, type RangeKey } from '../dates'
import { formatPercent, formatRupiah } from '../money'
import { errorMessage, t } from '../strings'
import { ReportsLocked } from './SubscriptionPage'

const dateTime = new Intl.DateTimeFormat('id-ID', { dateStyle: 'medium', timeStyle: 'short' })
const RANGES: { key: RangeKey; label: string }[] = [
  { key: 'today', label: t.today },
  { key: 'yesterday', label: t.yesterday },
  { key: 'last7', label: t.last7 },
  { key: 'month', label: t.thisMonth },
]

function useToday(): string | null {
  const biz = useQuery({ queryKey: ['business'], queryFn: endpoints.business })
  return biz.data ? dayIn(biz.data.outlet.timezone) : null
}

function RangeChips({ value, onChange }: { value: RangeKey; onChange: (k: RangeKey) => void }) {
  return (
    <div className="segmented" role="group" aria-label="Periode">
      {RANGES.map((r) => (
        <button key={r.key} type="button" className={r.key === value ? 'seg on' : 'seg'} aria-pressed={r.key === value} onClick={() => onChange(r.key)}>
          {r.label}
        </button>
      ))}
    </div>
  )
}

function Line({ title, sub, value }: { title: string; sub?: string; value: string }) {
  return (
    <li className="line">
      <div>
        <div className="line-title">{title}</div>
        {sub && <div className="muted small">{sub}</div>}
      </div>
      <div className="amount">{value}</div>
    </li>
  )
}

/** A report block: a heading and a list, one of the cards of the dashboard grid. */
function Block({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="card block">
      <h2>{title}</h2>
      {note && <p className="muted small">{note}</p>}
      {children}
    </section>
  )
}

/** The first [limit] lines of a long list, with the rest one tap away. */
function ShortList({ items, limit = 8 }: { items: ReactNode[]; limit?: number }) {
  const [all, setAll] = useState(false)
  const shown = all ? items : items.slice(0, limit)
  return (
    <>
      <ul className="list">{shown}</ul>
      {items.length > limit && (
        <button type="button" className="link" onClick={() => setAll((a) => !a)}>
          {all ? t.showLess : t.showAll(items.length)}
        </button>
      )}
    </>
  )
}

/** Sales summary, staff commission, payment methods, cash per session, phones (P4-05/06). */
export function HomePage() {
  const today = useToday()
  const [key, setKey] = useState<RangeKey>('today')
  const range = today ? rangeFor(key, today) : null
  const summary = useQuery({
    queryKey: ['summary', range],
    queryFn: () => endpoints.summary(range!.from, range!.to),
    enabled: range !== null,
  })
  const cash = useQuery({
    queryKey: ['cash', range],
    queryFn: () => endpoints.cashSessions(range!.from, range!.to),
    enabled: range !== null,
  })
  const phones = useQuery({ queryKey: ['sync-status'], queryFn: endpoints.syncStatus, refetchInterval: 60_000 })
  const conflicts = useQuery({ queryKey: ['conflicts'], queryFn: endpoints.conflicts })
  const [exportError, setExportError] = useState<string | null>(null)

  async function exportCsv(kind: 'sales' | 'items' | 'commission' | 'cash') {
    if (!range) return
    setExportError(null)
    try {
      await download(`/v1/exports/${kind}.csv?from=${range.from}&to=${range.to}`, `gammapos-${kind}-${range.from}-${range.to}.csv`)
    } catch (e) {
      setExportError(errorMessage(e instanceof Error ? e.message : 'network'))
    }
  }

  const s = summary.data
  const period = s ? (s.from === s.to ? formatDay(s.from) : `${formatDay(s.from)} – ${formatDay(s.to)}`) : ''
  return (
    <>
      <PageHeader title={t.navHome} subtitle={period || t.homeSub}>
        <RangeChips value={key} onChange={setKey} />
      </PageHeader>
      {(conflicts.data?.length ?? 0) > 0 && (
        <p className="warning">
          {t.conflictBanner(conflicts.data!.length)} <Link to="/data">{t.seeDetail}</Link>
        </p>
      )}
      {summary.isError && (summary.error.message === 'subscription_required' ? <ReportsLocked /> : <p className="error">{errorMessage(summary.error.message)}</p>)}
      {summary.isPending && range !== null && <p className="muted">{t.loading}</p>}

      {s && (
        <section className="stat-grid" aria-label={t.netSales}>
          <Stat tone="primary" label={t.netSales} value={formatRupiah(s.net)} note={period} />
          <Stat label={t.statCount} value={String(s.count)} />
          {s.discount > 0 && <Stat label={t.statDiscount} value={formatRupiah(s.discount)} />}
          {s.void_count > 0 && <Stat label={t.statVoid} value={formatRupiah(s.void_amount)} note={t.salesCount(s.void_count)} />}
          {s.tax > 0 && <Stat label={t.taxLine} value={formatRupiah(s.tax)} />}
          {s.service > 0 && <Stat label={t.serviceLine} value={formatRupiah(s.service)} />}
          {s.takeaway_fee > 0 && <Stat label={t.takeawayFeeLine} value={formatRupiah(s.takeaway_fee)} />}
          {s.rounding !== 0 && <Stat label={t.roundingLine} value={formatRupiah(s.rounding)} />}
        </section>
      )}
      {s && s.count === 0 && (
        <section className="card empty-card">
          <strong>{t.noSales}</strong>
          <p className="muted">{t.noSalesBody}</p>
        </section>
      )}
      {s && s.count > 0 && (
        <div className="dash-grid">
          {/* A noodle shop has no staff on its sales: a card of "Tanpa staf, tanpa komisi" would be noise. */}
          {s.by_staff.some((l) => l.staff_id !== null || l.commission > 0) && (
            <Block title={t.staffCommission} note={t.commissionNote}>
              <ul className="list">
                {s.by_staff.map((l) => (
                  <Line
                    key={l.staff_id ?? 'none'}
                    title={l.name || t.noStaff}
                    sub={`${t.services(l.services)} · ${formatRupiah(l.net)}`}
                    value={l.commission === 0 ? t.noCommission : formatRupiah(l.commission)}
                  />
                ))}
              </ul>
            </Block>
          )}
          <Block title={t.byMethod}>
            <ul className="list">
              {s.by_method.map((m) => (
                <Line key={m.method} title={t.methods[m.method] ?? m.method} value={formatRupiah(m.amount)} />
              ))}
            </ul>
          </Block>
          {s.by_order_type.some((o) => o.order_type !== null) && (
            <Block title={t.orderTypeTitle}>
              <ul className="list">
                {s.by_order_type.map((o) => (
                  <Line
                    key={o.order_type ?? 'none'}
                    title={o.order_type === 'dine_in' ? t.dineIn : o.order_type === 'takeaway' ? t.takeaway : t.noOrderType}
                    sub={t.salesCount(o.count)}
                    value={formatRupiah(o.net)}
                  />
                ))}
              </ul>
            </Block>
          )}
          {s.by_day.length > 1 && (
            <Block title={t.byDay}>
              <ShortList
                items={s.by_day.map((d) => (
                  <Line key={d.day} title={formatDay(d.day)} sub={t.salesCount(d.count)} value={formatRupiah(d.net)} />
                ))}
              />
            </Block>
          )}
          <Block title={t.byItem}>
            <ShortList
              items={s.by_item.map((i) => (
                <Line key={i.name} title={i.name} sub={t.sold(i.quantity)} value={formatRupiah(i.net)} />
              ))}
            />
          </Block>
        </div>
      )}
      <div className="dash-grid">
        {(cash.data?.length ?? 0) > 0 && (
          <Block title={t.cashTitle}>
            <ul className="list">
              {cash.data!.map((c) => (
                <li key={c.id} className="line">
                  <div>
                    <div className="line-title">
                      {formatDay(c.business_day)} · HP {c.device_letter}
                    </div>
                    <div className="muted small">
                      {t.cashLine(formatRupiah(c.opening_cash), formatRupiah(c.cash_sales), formatRupiah(c.cash_in + c.cash_out))}
                    </div>
                  </div>
                  <div className="amount right">
                    {c.closed_at === null ? (
                      <>
                        <div>{formatRupiah(c.expected_now)}</div>
                        <div className="muted small">{t.cashOpen}</div>
                      </>
                    ) : (
                      <>
                        <div>{formatRupiah(c.actual_cash ?? 0)}</div>
                        <div className={c.difference_amount ? 'small diff' : 'muted small'}>
                          {t.cashDiff} {formatRupiah(c.difference_amount ?? 0)}
                        </div>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Block>
        )}
        <Block title={t.phonesTitle}>
          <ul className="list">
            {phones.data
              ?.filter((p) => p.status === 'active')
              .map((p) => (
                <li key={p.id} className="line">
                  <div>
                    <div className="line-title">{t.phoneLine(p.letter)}</div>
                    <div className="muted small">
                      {t.lastSync}: {p.last_synced_at ? dateTime.format(new Date(p.last_synced_at)) : t.never}
                    </div>
                    {p.rejected > 0 && <div className="small diff">{t.phoneRejected(p.rejected)}</div>}
                  </div>
                </li>
              ))}
          </ul>
        </Block>
        <Block title={t.exportTitle} note={t.exportNote}>
          <div className="export-grid">
            {(
              [
                ['sales', t.exportSales],
                ['items', t.exportItems],
                ['commission', t.exportCommission],
                ['cash', t.exportCash],
              ] as const
            ).map(([kind, label]) => (
              <button key={kind} type="button" className="secondary" onClick={() => void exportCsv(kind)}>
                <Icon name="download" size={20} />
                {label}
              </button>
            ))}
          </div>
          {exportError && <p className="error">{exportError}</p>}
        </Block>
      </div>
    </>
  )
}

const methodNames = (methods: string | null) => (methods ? methods.split(',').map((m) => t.methods[m] ?? m).join(', ') : '')

/** Transaction list with search, newest first, 50 at a time: a table on a wide screen, cards on a phone. */
export function SalesPage() {
  const today = useToday()
  const [key, setKey] = useState<RangeKey>('today')
  const [q, setQ] = useState('')
  const range = today ? rangeFor(key, today) : null
  const pages = useInfiniteQuery({
    queryKey: ['sales', range, q],
    queryFn: ({ pageParam }) => endpoints.sales(range!.from, range!.to, q, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.length === 50 ? all.length * 50 : undefined),
    enabled: range !== null,
  })
  const rows = pages.data?.pages.flat() ?? []
  if (pages.isError && pages.error.message === 'subscription_required') return <ReportsLocked />
  return (
    <>
      <PageHeader title={t.salesTitle} subtitle={t.salesSub}>
        <RangeChips value={key} onChange={setKey} />
      </PageHeader>
      <div className="search-box">
        <Icon name="search" size={20} />
        <input
          type="search"
          placeholder={t.searchReceipt}
          aria-label={t.searchReceipt}
          value={q}
          onChange={(e) => setQ(e.target.value.toUpperCase())}
        />
      </div>
      {pages.isPending && range !== null && <p className="muted">{t.loading}</p>}
      {pages.isSuccess && rows.length === 0 && <p className="card muted">{t.noSales}</p>}
      {rows.length > 0 && (
        <div className="card table-card">
          <table className="data-table sales-table">
            <thead>
              <tr>
                <th scope="col">{t.colReceipt}</th>
                <th scope="col">{t.colWhen}</th>
                <th scope="col">{t.colCashier}</th>
                <th scope="col">{t.colOrder}</th>
                <th scope="col">{t.colMethod}</th>
                <th scope="col" className="right">
                  {t.colTotal}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const order = [r.order_type === 'dine_in' ? t.dineIn : r.order_type === 'takeaway' ? t.takeaway : null, r.table_label ? t.tableLabel(r.table_label) : null]
                  .filter(Boolean)
                  .join(' · ')
                return (
                  <tr key={r.id} className="click-row">
                    <td data-label={t.colReceipt}>
                      <Link to={`/transaksi/${r.id}`} className="row-link">
                        {r.receipt_no}
                      </Link>
                    </td>
                    <td data-label={t.colWhen} className="nowrap">
                      {formatDay(r.business_day)} {phoneTime(r.device_time, r.utc_offset_minutes)}
                    </td>
                    <td data-label={t.colCashier}>{r.cashier_name}</td>
                    <td data-label={t.colOrder}>{order}</td>
                    <td data-label={t.colMethod}>{methodNames(r.methods)}</td>
                    <td data-label={t.colTotal} className="right amount">
                      <span className={r.status === 'voided' ? 'struck' : ''}>{formatRupiah(r.net_amount)}</span>
                      {r.status === 'voided' && <span className="badge revoked">{t.voidedBadge}</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > 0 && <p className="muted small">{t.salesShown(rows.length)}</p>}
      {pages.hasNextPage && (
        <button className="secondary" disabled={pages.isFetchingNextPage} onClick={() => void pages.fetchNextPage()}>
          {t.loadMore}
        </button>
      )}
    </>
  )
}

export function SaleDetailPage() {
  const { id = '' } = useParams()
  const sale = useQuery({ queryKey: ['sale', id], queryFn: () => endpoints.sale(id) })
  if (sale.isError) return <p className="error">{errorMessage(sale.error.message)}</p>
  const s = sale.data
  if (!s) return <p>{t.loading}</p>
  const order = [s.order_type === 'dine_in' ? t.dineIn : s.order_type === 'takeaway' ? t.takeaway : null, s.table_label && t.tableLabel(s.table_label)]
    .filter(Boolean)
    .join(' · ')
  return (
    <>
      <PageHeader
        title={s.receipt_no}
        subtitle={`${formatDay(s.business_day)} ${phoneTime(s.device_time, s.utc_offset_minutes)} · HP ${s.device_letter}${s.cashier_name ? ` · ${t.cashier} ${s.cashier_name}` : ''}`}
        back={{ to: '/transaksi', label: t.back }}
      />
      {order && <p className="order-line">{order}</p>}
      {s.reversal && <p className="warning">{t.voidInfo(s.reversal.authorized_by_name || '-', s.reversal.reason)}</p>}
      <section className="card receipt-card">
        <ul className="list">
          {s.items.map((i, n) => (
            <Line
              key={n}
              title={`${i.quantity} × ${i.name}`}
              sub={[i.note && `${t.itemNote}: ${i.note}`, i.staff_name && t.by(i.staff_name), i.commission > 0 && `${t.commission} ${formatRupiah(i.commission)}`]
                .filter(Boolean)
                .join(' · ')}
              value={formatRupiah(i.gross)}
            />
          ))}
        </ul>
        <ul className="list totals">
          {(s.discount_amount > 0 || s.takeaway_fee_amount > 0 || s.service_amount > 0 || s.tax_amount > 0 || s.rounding_amount !== 0) && (
            <Line title={t.subtotal} value={formatRupiah(s.gross_amount)} />
          )}
          {s.discount_amount > 0 && <Line title={t.discount} value={`-${formatRupiah(s.discount_amount)}`} />}
          {s.takeaway_fee_amount > 0 && <Line title={t.takeawayFeeLine} value={formatRupiah(s.takeaway_fee_amount)} />}
          {s.service_amount > 0 && (
            <Line
              title={`${t.serviceLine} ${formatPercent(s.service_rate_bps)}%`}
              sub={s.prices_include_tax ? t.inPrice : undefined}
              value={formatRupiah(s.service_amount)}
            />
          )}
          {s.tax_amount > 0 && (
            <Line
              title={`${t.taxLine} ${formatPercent(s.tax_rate_bps)}%`}
              sub={s.prices_include_tax ? t.inPrice : undefined}
              value={formatRupiah(s.tax_amount)}
            />
          )}
          {s.rounding_amount !== 0 && <Line title={t.roundingLine} value={formatRupiah(s.rounding_amount)} />}
          <Line title={t.total} value={formatRupiah(s.net_amount)} />
          {s.payments.map((p, n) => (
            <Line key={n} title={t.methods[p.method] ?? p.method} value={formatRupiah(p.amount)} />
          ))}
        </ul>
      </section>
      <p className="muted small">
        {t.receivedAt}: {dateTime.format(new Date(s.server_received_at))}
      </p>
    </>
  )
}
